/**
 * The task picker at the desk: a select you can type into.
 *
 * A project with sixty tasks makes a native select a long scroll. This looks
 * like the select it replaces (it takes the same style), shows the chosen task
 * when idle, and on focus opens the grouped list; typing narrows it with the
 * same rule as the phone's TaskPicker (lib/taskOptions filterTaskGroups).
 * Arrows move, Enter picks, Esc puts back what was there.
 *
 * The list is portalled to <body> and placed with fixed coordinates, because
 * it lives inside popovers and modals that would otherwise clip it.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";

import { filterTaskGroups, type TaskGroup } from "@/lib/taskOptions";

export function TaskCombo({
  value, onChange, groups, disabled, style, emptyLabel = "— No specific task", ariaLabel = "Task",
}: {
  value: string;
  onChange: (next: string) => void;
  groups: TaskGroup[];
  disabled?: boolean;
  /** The style of the select this replaces, so it sits in its row unchanged. */
  style?: CSSProperties;
  emptyLabel?: string;
  ariaLabel?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  /** null while closed; the typed text while open. */
  const [query, setQuery] = useState<string | null>(null);
  const [active, setActive] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const open = query !== null;

  const selected = useMemo(() => {
    for (const g of groups) {
      const t = g.tasks.find((x) => x.id === value);
      if (t) return t;
    }
    return undefined;
  }, [groups, value]);

  const filtered = useMemo(() => filterTaskGroups(groups, query ?? ""), [groups, query]);
  /** Every pickable row in display order; "" is the no-task row. */
  const flat = useMemo(() => {
    const ids = query?.trim() ? [] : [""];
    for (const g of filtered) for (const t of g.tasks) ids.push(t.id);
    return ids;
  }, [filtered, query]);

  // Follow the input while open: the page or a modal may scroll under it.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => inputRef.current && setRect(inputRef.current.getBoundingClientRect());
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-row="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  function openList() {
    setQuery("");
    setActive(Math.max(0, flat.indexOf(value)));
  }

  function close() {
    setQuery(null);
  }

  function pick(id: string) {
    onChange(id);
    close();
    inputRef.current?.blur();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter") { e.preventDefault(); openList(); }
      return;
    }
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(flat.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (flat[active] !== undefined) pick(flat[active]);
    } else if (e.key === "Escape") {
      // Inside a modal, Esc closes the list, not the modal.
      e.stopPropagation();
      close();
      inputRef.current?.blur();
    }
  }

  const below = rect ? window.innerHeight - rect.bottom : 0;
  const up = rect ? below < 240 && rect.top > below : false;
  const maxHeight = rect ? Math.min(320, Math.max(140, (up ? rect.top : below) - 12)) : 320;

  let row = 0;
  const rowStyle = (i: number, on: boolean): CSSProperties => ({
    display: "block", width: "100%", padding: "7px 12px", textAlign: "left",
    border: "none", cursor: "pointer", fontSize: 13,
    background: i === active ? "var(--accentTint)" : "transparent",
    color: on ? "var(--accent)" : "var(--ink)", fontWeight: on ? 600 : 400,
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  });

  return (
    <>
      <input
        ref={inputRef}
        role="combobox"
        aria-expanded={open}
        aria-label={ariaLabel}
        disabled={disabled}
        value={open ? query : (selected?.title ?? "")}
        placeholder={open ? "Type to find a task…" : emptyLabel}
        onFocus={() => { if (!open) openList(); }}
        onClick={() => { if (!open) openList(); }}
        onBlur={close}
        onChange={(e) => { setQuery(e.target.value); setActive(0); }}
        onKeyDown={onKeyDown}
        style={{
          ...style,
          cursor: disabled ? "default" : "text",
          textOverflow: "ellipsis",
          // The placeholder doubles as the "no task" label, so it reads as a value.
          ...(selected || open ? null : { color: "var(--muted)" }),
        }}
      />
      {open && rect && createPortal(
        <div
          ref={listRef}
          role="listbox"
          // Keep focus in the input so a click picks rather than blurs first,
          // and keep the click from reading as "outside" to the popover the
          // input sits in (the list is portalled, so it is outside in the DOM).
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
          style={{
            position: "fixed", zIndex: 1000,
            left: rect.left, width: Math.max(rect.width, 260),
            ...(up ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
            maxHeight, overflowY: "auto",
            background: "var(--raised)", border: "1px solid var(--overlayLine)", borderRadius: 10,
            boxShadow: "var(--shadowOverlay)", padding: "4px 0",
          }}
        >
          {!query?.trim() && (() => {
            const i = row++;
            return (
              <button type="button" data-row={i} onMouseEnter={() => setActive(i)} onClick={() => pick("")}
                style={{ ...rowStyle(i, false), color: "var(--muted)" }}>{emptyLabel}</button>
            );
          })()}
          {filtered.map((g) => (
            <div key={g.phaseId}>
              {groups.length > 1 && (
                <div style={{
                  padding: "8px 12px 3px", fontSize: 10.5, letterSpacing: "0.06em",
                  textTransform: "uppercase", color: "var(--muted2)",
                }}>{g.phaseName}</div>
              )}
              {g.tasks.map((t) => {
                const i = row++;
                return (
                  <button key={t.id} type="button" data-row={i} title={t.title}
                    onMouseEnter={() => setActive(i)} onClick={() => pick(t.id)}
                    style={rowStyle(i, t.id === value)}>{t.title}</button>
                );
              })}
            </div>
          ))}
          {filtered.length === 0 && (
            <div style={{ padding: "8px 12px", fontSize: 13, color: "var(--muted)" }}>
              No task matches “{query?.trim()}”
            </div>
          )}
        </div>,
        document.body,
      )}
    </>
  );
}
